import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { searchPortalOrganizations, getImageUrl } from '../utils/api';

function clipDescription(text, max = 140) {
    if (!text || typeof text !== 'string') return '';
    const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (plain.length <= max) return plain;
    return `${plain.slice(0, max)}…`;
}

export default function ZehumaPortalHome() {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const wrapRef = useRef(null);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await searchPortalOrganizations(q);
                setResults(Array.isArray(res.data) ? res.data : []);
                setOpen(true);
            } catch (e) {
                console.error(e);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const goMenu = (slug) => {
        setOpen(false);
        navigate(`/${slug}`);
    };

    return (
        <div className="relative">
            {/* overflow-y kesilmesin: taşan arama listesi üst katmanda kalsın. Gradient ayrı sarmalayıcıda overflow-hidden. */}
            <section className="relative z-[25] px-4 pb-16 pt-10 sm:pb-24 sm:pt-16">
                <div
                    className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.12]"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 20% 20%, #7c3aed 0%, transparent 45%), radial-gradient(circle at 80% 10%, #ea580c 0%, transparent 40%), radial-gradient(circle at 50% 80%, #0891b2 0%, transparent 45%)'
                    }}
                />
                <div className="relative z-10 mx-auto max-w-3xl text-center">
                    <p className="text-sm font-semibold uppercase tracking-[0.25em] text-violet-600">Dijital menü</p>
                    <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
                        Menünüz tek{' '}
                        <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                            QR
                        </span>{' '}
                        kodda
                    </h1>
                    <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 sm:text-lg">
                        Restoranınızı arayın, anında sonuçlardan birine dokunun ve dijital menüye geçin.
                    </p>

                    <div ref={wrapRef} className="relative z-20 mx-auto mt-10 max-w-lg">
                        <label htmlFor="portal-search" className="sr-only">
                            Restoran ara
                        </label>
                        <div className="flex rounded-2xl border border-slate-200 bg-white shadow-lg shadow-violet-500/10 ring-1 ring-slate-100 focus-within:ring-2 focus-within:ring-violet-500/40">
                            <span className="flex items-center pl-4 text-slate-400" aria-hidden="true">
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </span>
                            <input
                                id="portal-search"
                                type="search"
                                autoComplete="off"
                                placeholder="Restoran veya slug ara…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onFocus={() => query.trim().length >= 2 && setOpen(true)}
                                className="min-h-[52px] flex-1 border-0 bg-transparent px-3 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                            />
                            {loading && (
                                <span className="flex items-center pr-4 text-violet-600">
                                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                </span>
                            )}
                        </div>

                        {open && query.trim().length >= 2 && (
                            <ul
                                className="absolute left-0 right-0 top-full z-[70] mt-2 max-h-[min(70vh,28rem)] overflow-auto rounded-2xl border border-slate-200 bg-white py-2 text-left shadow-xl ring-1 ring-slate-900/5"
                                role="listbox"
                            >
                                {results.length === 0 && !loading && (
                                    <li className="px-4 py-3 text-sm text-slate-500">Sonuç bulunamadı.</li>
                                )}
                                {results.map((org) => {
                                    const desc = clipDescription(org.restaurant_description);
                                    const logoPath = org.display_logo || org.restaurant_logo;
                                    const showLogo = Boolean(logoPath && String(logoPath).trim());
                                    const qrLive = Boolean(org.qr_menu_active);
                                    return (
                                        <li key={org.id}>
                                            <button
                                                type="button"
                                                className="flex w-full items-stretch gap-3 px-3 py-3 text-left transition hover:bg-violet-50 active:bg-violet-100 sm:px-4"
                                                onClick={() => goMenu(org.slug)}
                                            >
                                                <div className="relative h-14 w-14 shrink-0 self-center overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                                                    {showLogo ? (
                                                        <img
                                                            src={getImageUrl(logoPath)}
                                                            alt=""
                                                            className="h-full w-full object-cover"
                                                            loading="lazy"
                                                            decoding="async"
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-lg font-bold text-violet-400">
                                                            {(org.name || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1 self-center">
                                                    <div className="font-semibold text-slate-900">{org.name}</div>
                                                    {org.restaurant_name && org.restaurant_name !== org.name && (
                                                        <p className="mt-0.5 text-xs text-slate-500">{org.restaurant_name}</p>
                                                    )}
                                                    {org.title && <p className="mt-0.5 text-xs font-medium text-violet-700">{org.title}</p>}
                                                    {desc && (
                                                        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-600">{desc}</p>
                                                    )}
                                                    <p className="mt-1 font-mono text-[11px] text-violet-600">/{org.slug}</p>
                                                </div>
                                                {qrLive && (
                                                    <div className="flex min-w-[6.75rem] max-w-[8.5rem] shrink-0 flex-col items-center justify-center border-l border-slate-100 pl-2 sm:min-w-[7.5rem] sm:pl-3">
                                                        <span className="inline-flex items-center justify-center rounded-full bg-emerald-100 px-2 py-1 text-center text-[9px] font-bold uppercase leading-tight tracking-wide text-emerald-800 ring-1 ring-emerald-200/80 sm:text-[10px]">
                                                            QR menü aktif
                                                        </span>
                                                    </div>
                                                )}
                                                <span className="flex shrink-0 items-center self-center text-slate-400" aria-hidden="true">
                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {query.trim().length > 0 && query.trim().length < 2 && (
                            <p className="mt-2 text-center text-xs text-slate-500">Aramak için en az 2 karakter yazın.</p>
                        )}
                    </div>
                </div>
            </section>

            <section className="relative z-[1] border-y border-slate-200 bg-white px-4 py-14 sm:py-16">
                <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center sm:flex-row sm:justify-between sm:text-left">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">QR Menü oluştur</h2>
                        <p className="mt-2 max-w-md text-sm text-slate-600 sm:text-base">
                            İşletmeniz için dijital menü, kampanyalar ve analitik. Teklif almak için bize yazın.
                        </p>
                    </div>
                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                        <a
                            href="mailto:hello@zehuma.com?subject=Zehuma%20QR%20Menü%20Talebi"
                            className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:brightness-105"
                        >
                            Teklif iste
                        </a>
                        <a
                            href="tel:08503023545"
                            className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                        >
                            0850 302 35 45
                        </a>
                    </div>
                </div>
                <p className="mx-auto mt-8 max-w-2xl text-center text-xs text-slate-500">
                    Yönetim paneli için{' '}
                    <Link to="/admin/login" className="font-medium text-violet-600 hover:underline">
                        işletme girişi
                    </Link>
                    .
                </p>
            </section>
        </div>
    );
}
